/** biome-ignore-all lint/complexity/noStaticOnlyClass: echarts */
import type { TelemetryDataValues } from 'commons';
import type { MarkAreaComponentOption } from 'echarts';

/**
 * Predefined colors for test phases with their visual properties
 */
export const TEST_PHASE_COLORS: Record<string | number, { background: string; border: string; label: string }> = {
  0: {
    background: 'rgba(200, 200, 200, 0.3)',
    border: '#888888',
    label: '#666666',
  }, // Idle/Unknown
  1: {
    background: 'rgba(76, 175, 80, 0.3)',
    border: '#4CAF50',
    label: '#2E7D32',
  }, // Phase 1 - Green
  2: {
    background: 'rgba(33, 150, 243, 0.3)',
    border: '#2196F3',
    label: '#1565C0',
  }, // Phase 2 - Blue
  3: {
    background: 'rgba(255, 152, 0, 0.3)',
    border: '#FF9800',
    label: '#E65100',
  }, // Phase 3 - Orange
  4: {
    background: 'rgba(156, 39, 176, 0.3)',
    border: '#9C27B0',
    label: '#6A1B9A',
  }, // Phase 4 - Purple
  5: {
    background: 'rgba(244, 67, 54, 0.3)',
    border: '#F44336',
    label: '#C62828',
  }, // Phase 5 - Red
  6: {
    background: 'rgba(0, 188, 212, 0.3)',
    border: '#00BCD4',
    label: '#00838F',
  }, // Phase 6 - Cyan
  7: {
    background: 'rgba(255, 235, 59, 0.3)',
    border: '#FFEB3B',
    label: '#F57F17',
  }, // Phase 7 - Yellow
  8: {
    background: 'rgba(121, 85, 72, 0.3)',
    border: '#795548',
    label: '#4E342E',
  }, // Phase 8 - Brown
  9: {
    background: 'rgba(96, 125, 139, 0.3)',
    border: '#607D8B',
    label: '#37474F',
  }, // Phase 9 - Blue Grey
};

/**
 * Represents a single test phase interval with start/end times
 */
export interface TestPhaseInterval {
  readonly startTime: Date;
  readonly endTime: Date;
  readonly phaseValue: string | number;
  readonly phaseName: string;
}

/**
 * Configuration options for test phase mark areas
 */
export interface TestPhaseMarkAreaConfig {
  readonly xAxisIndex?: number;
  readonly customColors?: Record<string | number, { background: string; border: string; label: string }>;
  readonly labelPrefix?: string;
}

/**
 * Class responsible for converting test_state telemetry data into ECharts mark areas
 * for visualizing test phases on the chart.
 */
export class TestPhaseMarkArea {
  /**
   * Converts test_state telemetry data into mark area configuration for ECharts.
   * Groups consecutive data points with the same phase value into intervals.
   *
   * @param telemetryData - Array of telemetry data containing test_state values
   * @param testStateField - The field name for the test state (default: 'test_state')
   * @param config - Optional configuration for mark area appearance
   * @returns ECharts MarkAreaComponentOption for rendering test phases
   */
  static convert(telemetryData: TelemetryDataValues[], testStateField = 'test_state', config: TestPhaseMarkAreaConfig = {}): MarkAreaComponentOption {
    if (!telemetryData || telemetryData.length === 0) {
      return { data: [] };
    }

    const intervals = TestPhaseMarkArea.extractPhaseIntervals(telemetryData, testStateField, config.labelPrefix);
    const markAreaData = TestPhaseMarkArea.convertIntervalsToMarkAreas(intervals, config);

    return {
      data: markAreaData,
    };
  }

  /**
   * Extracts phase intervals from sequential telemetry data.
   * Groups consecutive data points with the same phase value.
   *
   * @param telemetryData - Sorted telemetry data (by datetime)
   * @param testStateField - The field name containing the phase value
   * @param labelPrefix - Optional prefix for phase labels
   * @returns Array of test phase intervals
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: data processing logic
  static extractPhaseIntervals(telemetryData: TelemetryDataValues[], testStateField: string, labelPrefix = 'Phase'): TestPhaseInterval[] {
    const sortedData = [...telemetryData].sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());

    const intervals: TestPhaseInterval[] = [];
    let currentPhase: string | number | null = null;
    let intervalStart: Date | null = null;

    for (const entry of sortedData) {
      const phaseValue = entry[testStateField];
      const timestamp = new Date(entry.datetime);

      if (phaseValue === null || phaseValue === undefined) {
        // Close current interval if phase becomes null
        if (currentPhase !== null && intervalStart !== null) {
          intervals.push({
            startTime: intervalStart,
            endTime: timestamp,
            phaseValue: currentPhase,
            phaseName: TestPhaseMarkArea.formatPhaseName(currentPhase, labelPrefix),
          });
          currentPhase = null;
          intervalStart = null;
        }
        continue;
      }

      if (phaseValue !== currentPhase) {
        // Close previous interval
        if (currentPhase !== null && intervalStart !== null) {
          intervals.push({
            startTime: intervalStart,
            endTime: timestamp,
            phaseValue: currentPhase,
            phaseName: TestPhaseMarkArea.formatPhaseName(currentPhase, labelPrefix),
          });
        }

        // Start new interval
        currentPhase = phaseValue;
        intervalStart = timestamp;
      }
    }

    // Close the last interval using the last data point's timestamp
    const endTime = sortedData.at(-1);
    if (currentPhase !== null && intervalStart !== null && sortedData.length > 0 && endTime) {
      intervals.push({
        startTime: intervalStart,
        endTime: new Date(endTime.datetime),
        phaseValue: currentPhase,
        phaseName: TestPhaseMarkArea.formatPhaseName(currentPhase, labelPrefix),
      });
    }

    return intervals;
  }

  /**
   * Converts phase intervals into ECharts mark area data format.
   *
   * @param intervals - Array of test phase intervals
   * @param config - Configuration for mark area appearance
   * @returns Array of mark area data tuples
   */
  private static convertIntervalsToMarkAreas(
    intervals: TestPhaseInterval[],
    config: TestPhaseMarkAreaConfig
  ): [{ xAxis: Date; itemStyle: object; label?: object }, { xAxis: Date }][] {
    const colors = config.customColors ?? TEST_PHASE_COLORS;

    return intervals.map((interval) => {
      const phaseColors = colors[interval.phaseValue] ?? colors[0];

      return [
        {
          xAxis: interval.startTime,
          itemStyle: {
            color: phaseColors.background,
            borderColor: phaseColors.border,
            borderWidth: 1,
            borderType: 'solid',
          },
          emphasis: {
            disabled: true,
            label: {
              position: ['50%', '50%'],
            },
          },
          label: {
            show: true,
            position: ['50%', '50%'],
            rotate: 90,
            distance: 15,
            color: phaseColors.label,
            fontSize: 11,
            fontWeight: 'bold',
            formatter: interval.phaseName,
            align: 'center',
            verticalAlign: 'middle',
          },
        },
        {
          xAxis: interval.endTime,
        },
      ];
    }) as [{ xAxis: Date; itemStyle: object; label?: object }, { xAxis: Date }][];
  }

  /**
   * Formats the phase name for display in the label.
   *
   * @param phaseValue - The raw phase value from telemetry
   * @param prefix - Prefix to use for the label
   * @returns Formatted phase name string
   */
  private static formatPhaseName(phaseValue: string | number, prefix: string): string {
    if (typeof phaseValue === 'string' && phaseValue.length > 0) {
      // If it's already a descriptive string, use it directly
      if (Number.isNaN(Number(phaseValue))) {
        return phaseValue;
      }
    }
    return `${prefix} ${phaseValue}`;
  }

  /**
   * Checks if a control represents a test_state channel.
   *
   * @param label - The label/name of the control
   * @returns True if this is a test_state channel
   */
  static isTestStateChannel(label: string): boolean {
    return label?.toLowerCase() === 'test_state';
  }
}
